import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useCustomerAuth } from "./CustomerAuthContext";
import { clearCart, getCart, removeCartItem, upsertCartItem } from "../services/cartService";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "cynos_cart_v1";
const CART_OWNER_KEY = "cynos_cart_owner_v1";

const itemKey = (productId, size = "", color = "") =>
  `${String(productId || "")}::${String(size || "").toLowerCase()}::${String(color || "").toLowerCase()}`;

const sameVariant = (item, productId, size, color = "") =>
  itemKey(item.product_id, item.size, item.color) === itemKey(productId, size, color);

const readCart = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed)
      ? parsed.map((item) => ({ ...item, price: Number(item.price || 0), qty: Math.max(1, Number(item.qty) || 1) }))
      : [];
  } catch {
    return [];
  }
};

const colorImageKey = (colorImages, color) =>
  Object.keys(colorImages || {}).find((key) => key.toLowerCase() === String(color || "").toLowerCase());

const variantSkuFor = (variantSkus, color, size) => {
  const entries = variantSkus || {};
  const candidates = [`${color}|${size}`, `${color}-${size}`, `${color}_${size}`, `${color} ${size}`, size].filter(Boolean);
  const lowered = Object.fromEntries(Object.entries(entries).map(([key, value]) => [String(key).toLowerCase(), value]));
  return candidates.map((key) => lowered[String(key).toLowerCase()]).find(Boolean) || "";
};

const cartItemFromProduct = (product, size, qty = 1, color = "") => {
  const selectedColor = String(color || "").trim();
  const selectedColorImageKey = colorImageKey(product.color_images, selectedColor);
  const selectedColorImages = selectedColorImageKey ? product.color_images[selectedColorImageKey] : [];
  const selectedImage = selectedColorImages?.[0] || product.images?.[0] || product.image || "";
  const selectedSku = variantSkuFor(product.variant_skus, selectedColor, size) || product.qikink_sku || product.sku || "";

  return {
    product_id: product.id || product.product_id,
    slug: product.slug || "",
    name: product.name || "Product",
    price: Number(product.price || 0),
    original_price: product.original_price,
    image: selectedImage,
    sku: selectedSku,
    size,
    color: selectedColor,
    sizes: Array.isArray(product.sizes) ? product.sizes : [],
    colors: Array.isArray(product.colors) ? product.colors : [],
    color_images: product.color_images || {},
    variant_skus: product.variant_skus || {},
    variant_details: {
      qikink_designs: product.qikink_designs || [],
      selected_color: selectedColor,
      selected_sku: selectedSku,
      selected_color_images: selectedColorImages || [],
    },
    qty: Math.max(1, Number(qty) || 1),
  };
};

const cartItemVariant = (item, size, color = "") => {
  const selectedColor = String(color || "").trim();
  const selectedColorImageKey = colorImageKey(item.color_images, selectedColor);
  const selectedColorImages = selectedColorImageKey ? item.color_images[selectedColorImageKey] : [];
  const selectedImage = selectedColorImages?.[0] || item.image || "";
  const selectedSku = variantSkuFor(item.variant_skus, selectedColor, size) || item.sku || "";

  return {
    ...item,
    size,
    color: selectedColor,
    image: selectedImage,
    sku: selectedSku,
    variant_details: {
      ...item.variant_details,
      selected_color: selectedColor,
      selected_sku: selectedSku,
      selected_color_images: selectedColorImages || [],
    },
  };
};

const cartItemsFromApi = (cart) =>
  (cart?.items || []).filter((entry) => entry.product).map((entry) =>
    cartItemFromProduct(entry.product, entry.selectedSize || "", entry.qty, entry.selectedColor || "")
  );

export const CartProvider = ({ children }) => {
  const { customer, isAuthenticated, loading: authLoading } = useCustomerAuth();
  const [items, setItems] = useState(() => readCart());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingKeys, setPendingKeys] = useState({});
  const itemsRef = useRef(items);

  const commitItems = useCallback((nextItems) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;

    const syncCart = async () => {
      if (!isAuthenticated) {
        if (localStorage.getItem(CART_OWNER_KEY)) {
          localStorage.removeItem(CART_OWNER_KEY);
          commitItems([]);
        } else {
          commitItems(readCart());
        }
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const ownerId = String(customer?.id || "");
        const shouldMergeGuestCart = localStorage.getItem(CART_OWNER_KEY) !== ownerId;
        const localItems = readCart();

        if (shouldMergeGuestCart && localItems.length) {
          await Promise.allSettled(
            localItems.map((item) =>
              upsertCartItem({
                product_id: item.product_id,
                color: item.color || "",
                size: item.size || "",
                qty: item.qty,
              })
            )
          );
        }

        const cart = await getCart();
        if (!cancelled) {
          localStorage.setItem(CART_OWNER_KEY, ownerId);
          commitItems(cartItemsFromApi(cart));
        }
      } catch (err) {
        console.warn("Cart synchronization failed", err);
        if (!cancelled) toast.error("Unable to sync your cart. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    syncCart();
    return () => {
      cancelled = true;
    };
  }, [authLoading, commitItems, customer?.id, isAuthenticated]);

  const setPending = useCallback((key, pending) => {
    setPendingKeys((current) => ({ ...current, [key]: pending }));
  }, []);

  const addItem = useCallback(
    async (product, size, qty = 1, color = "", openDrawer = true) => {
      if ([0, 2].includes(Number(product.active_status))) return false;
      const nextItem = cartItemFromProduct(product, size, qty, color);
      if (!nextItem.product_id) return false;

      const key = itemKey(nextItem.product_id, size, color);
      if (pendingKeys[key]) return false;

      const previousItem = itemsRef.current.find((item) => sameVariant(item, nextItem.product_id, size, color));
      const nextQty = (previousItem?.qty || 0) + nextItem.qty;
      const optimisticItem = { ...(previousItem || nextItem), qty: nextQty };
      commitItems(
        previousItem
          ? itemsRef.current.map((item) => (sameVariant(item, nextItem.product_id, size, color) ? optimisticItem : item))
          : [...itemsRef.current, optimisticItem]
      );
      if (openDrawer) setOpen(true);

      if (!isAuthenticated) return true;

      setPending(key, true);
      try {
        await upsertCartItem({ product_id: nextItem.product_id, size, color, qty: nextQty });
        return true;
      } catch (err) {
        commitItems(
          previousItem
            ? itemsRef.current.map((item) => (sameVariant(item, nextItem.product_id, size, color) ? previousItem : item))
            : itemsRef.current.filter((item) => !sameVariant(item, nextItem.product_id, size, color))
        );
        toast.error("Unable to update cart. Please try again.");
        return false;
      } finally {
        setPending(key, false);
      }
    },
    [commitItems, isAuthenticated, pendingKeys, setPending]
  );

  const updateQty = useCallback(
    async (productId, size, color, qty) => {
      if (qty === undefined) {
        qty = color;
        color = "";
      }
      const nextQty = Math.max(1, Number(qty) || 1);
      const key = itemKey(productId, size, color);
      if (pendingKeys[key]) return false;

      const previousItem = itemsRef.current.find((item) => sameVariant(item, productId, size, color));
      if (!previousItem || previousItem.qty === nextQty) return false;

      commitItems(
        itemsRef.current.map((item) =>
          sameVariant(item, productId, size, color) ? { ...item, qty: nextQty } : item
        )
      );
      if (!isAuthenticated) return true;

      setPending(key, true);
      try {
        await upsertCartItem({ product_id: productId, size, color, qty: nextQty });
        return true;
      } catch (err) {
        commitItems(
          itemsRef.current.map((item) => (sameVariant(item, productId, size, color) ? previousItem : item))
        );
        toast.error("Unable to update cart. Please try again.");
        return false;
      } finally {
        setPending(key, false);
      }
    },
    [commitItems, isAuthenticated, pendingKeys, setPending]
  );

  const updateItemVariant = useCallback(
    async (productId, size, color, nextSize, nextColor) => {
      const previousKey = itemKey(productId, size, color);
      const nextKey = itemKey(productId, nextSize, nextColor);
      if (previousKey === nextKey) return true;
      if (pendingKeys[previousKey] || pendingKeys[nextKey]) return false;

      const previousItem = itemsRef.current.find((item) => sameVariant(item, productId, size, color));
      if (!previousItem) return false;

      const existingItem = itemsRef.current.find((item) => sameVariant(item, productId, nextSize, nextColor));
      const nextItem = cartItemVariant(previousItem, nextSize, nextColor);
      const mergedItem = existingItem
        ? { ...existingItem, qty: existingItem.qty + previousItem.qty }
        : nextItem;

      const optimisticItems = itemsRef.current.filter(
        (item) => !sameVariant(item, productId, size, color) && !sameVariant(item, productId, nextSize, nextColor)
      );
      optimisticItems.push(mergedItem);
      const previousItems = itemsRef.current;
      commitItems(optimisticItems);

      if (!isAuthenticated) return true;

      setPending(previousKey, true);
      if (nextKey !== previousKey) setPending(nextKey, true);
      try {
        await upsertCartItem({ product_id: productId, size: nextSize, color: nextColor, qty: mergedItem.qty });
        await removeCartItem({ product_id: productId, size, color });
        return true;
      } catch (err) {
        commitItems(previousItems);
        toast.error("Unable to update variant. Please try again.");
        return false;
      } finally {
        setPending(previousKey, false);
        if (nextKey !== previousKey) setPending(nextKey, false);
      }
    },
    [commitItems, isAuthenticated, pendingKeys, setPending]
  );

  const removeItem = useCallback(
    async (productId, size, color = "") => {
      const key = itemKey(productId, size, color);
      if (pendingKeys[key]) return false;
      const previousItem = itemsRef.current.find((item) => sameVariant(item, productId, size, color));
      if (!previousItem) return false;

      commitItems(itemsRef.current.filter((item) => !sameVariant(item, productId, size, color)));
      if (!isAuthenticated) return true;

      setPending(key, true);
      try {
        await removeCartItem({ product_id: productId, size, color });
        return true;
      } catch (err) {
        commitItems([...itemsRef.current, previousItem]);
        toast.error("Unable to update cart. Please try again.");
        return false;
      } finally {
        setPending(key, false);
      }
    },
    [commitItems, isAuthenticated, pendingKeys, setPending]
  );

  const clear = useCallback(async () => {
    const previousItems = itemsRef.current;
    commitItems([]);
    if (!isAuthenticated) return true;

    try {
      await clearCart();
      return true;
    } catch (err) {
      commitItems(previousItems);
      toast.error("Unable to update cart. Please try again.");
      return false;
    }
  }, [commitItems, isAuthenticated]);

  const subtotal = items.reduce((acc, item) => acc + item.price * item.qty, 0);
  const totalQty = items.reduce((acc, item) => acc + item.qty, 0);
  const freeCount = Math.floor(totalQty / 3);
  const unitPrices = items.flatMap((item) => Array.from({ length: item.qty }, () => item.price)).sort((a, b) => a - b);
  const discount = unitPrices.slice(0, freeCount).reduce((sum, price) => sum + price, 0);
  const total = Math.max(0, subtotal - discount);

  const value = useMemo(
    () => ({
      items,
      addItem,
      updateQty,
      updateItemVariant,
      removeItem,
      clear,
      subtotal,
      discount,
      total,
      totalQty,
      open,
      setOpen,
      loading,
      pendingKeys,
    }),
    [addItem, clear, discount, items, loading, open, pendingKeys, removeItem, subtotal, total, totalQty, updateItemVariant, updateQty]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
